<script setup lang="ts">
/**
 * Six things the studio does. The icon tile carries the half of the loop it belongs to.
 * Only what is built is promised: Markdown and plain text are the formats a document is read from.
 */
import Icon from '~/components/ui/Icon.vue'
import type { IconName } from '~/utils/icons'

const ITEMS: { title: string; body: string; icon: IconName; half: 'build' | 'serve' }[] = [
  { title: 'Documents become training examples', icon: 'data', half: 'build', body: 'Upload Markdown and plain-text files. Training reads them and writes the question–answer pairs it learns from; you start one run, not a preparation step.' },
  { title: 'Trained, not searched', icon: 'zap', half: 'build', body: 'LoRA on a license-clean open-weight base. Settings are derived from your data or set by hand; time and price are estimated before the run starts.' },
  { title: 'See what it learned', icon: 'compare', half: 'serve', body: 'Held-out questions from your own documents, answered by the base and by your model, side by side. The model card reports what was measured.' },
  { title: 'Memory for facts that change', icon: 'memory', half: 'serve', body: 'Training carries voice and terminology. For prices, rules and stock, attach a memory collection — answers come back with a citation into the source.' },
  { title: 'Three ways to use it', icon: 'download', half: 'serve', body: 'Answer in the playground, call an OpenAI-compatible endpoint with a key, or download the weights and run them on your own machine.' },
  { title: 'Metered, on a ledger', icon: 'gauge', half: 'build', body: 'Every figure on the billing page comes from the usage ledger. $5 of credit every month; no plans, no seats.' },
]
</script>

<template>
  <section id="features" class="border-b border-border">
    <div class="mx-auto max-w-[var(--measure-1120px)] px-6 py-16">
      <div class="max-w-[var(--measure-560px)]">
        <span class="num text-[length:var(--type-fine)] font-semibold text-primary">What it does</span>
        <h2 class="m-0 mt-2 text-[length:var(--measure-2rem)] font-bold leading-[1.1] tracking-[-0.025em] text-pretty">From a folder of documents to a model that answers like your team.</h2>
      </div>
      <div class="mt-8 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        <article v-for="it in ITEMS" :key="it.title" class="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 transition hover:border-primary/40">
          <span class="grid size-8 place-items-center rounded-[var(--measure-8px)]" :class="it.half === 'build' ? 'bg-primary/10 text-primary' : 'bg-info/10 text-info'">
            <Icon :name="it.icon" :size="15" />
          </span>
          <span class="text-[length:var(--measure-1rem)] font-semibold leading-snug">{{ it.title }}</span>
          <p class="m-0 text-[length:var(--measure-12p5px)] leading-relaxed text-muted-foreground text-pretty">{{ it.body }}</p>
        </article>
      </div>
    </div>
  </section>
</template>
